<template>
    <div>
        <Modal
            :show="modalVisibility"
            :overflowHidden="false"
            class="overflow-visible"
            @close="closeModal"
        >
            <div class="flex flex-col pr-6 pl-2 py-2 text-default-1">
                <p class="text-body-4 font-normal text-default-1">Move to folder</p>
                <div class="xxs:p-4 bg-white rounded border border-white lg:px-0 lg:pt-4 lg:pb-0 box-border w-full">

                    <div class="w-full" v-if="loadingData">
                        <div class="w-full h-8 first:mt-0 mt-1 bg-black/10 rounded skeleton-loader" v-for="i in 5" :key="i">
                        </div>
                    </div>

                    <FolderRow
                        v-if="!loadingData"
                        :document="rootFolder"
                        :selected-folder="targetFolder"
                        @select="selectFolder"
                        @toggle="toggleFolder"
                    />

                    <ul class="pl-3">
                        <li
                            class="w-full flex justify-center flex-col"
                            v-for="(document) in formattedDocuments"
                            :key="document.documentable.uid"
                        >
                            <FolderRow
                                :document="document"
                                :selected-folder="targetFolder"
                                @select="selectFolder"
                                @toggle="toggleFolder"
                                :loaders="loaders"
                            />
                        </li>
                    </ul>

                    <div class="w-full flex items-center mt-6">
                        <Button
                            :disabled="loadingData || !targetFolder || movingLoader"
                            class="xxs:w-full lg:w-auto self-start"
                            buttonType="primary"
                            :loading="movingLoader"
                            @click="moveDocument"
                        >Move</Button>

                        <Button
                            class="xxs:w-full lg:w-auto self-start ml-4"
                            buttonType="ghost"
                            @click="closeModal"
                        >Cancel</Button>
                    </div>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import Modal from "@/components/core/Modal.vue";
import Button from "@/components/core/Button.vue";
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import {useDocuments} from "@/composables/useDocuments";
import FolderRow from "@/components/custom/documents/FolderRow.vue";
import {toast} from "vue3-toastify";

const {list} = useDocuments();

const {organization} = storeToRefs(useAuth());

const emit = defineEmits(['close', 'move']);

const targetFolder = ref(null);

const props = defineProps({
    modalVisibility: {
        type: Boolean,
        default: false,
    },
    movingLoader: {
        type: Boolean,
        default: false,
    },
    selectedDocuments: {
        type: Object,
        default: () => {},
    },
    // single file is used when deleting a single file through the options menu
    singleFile: {
        type: Object,
        default: null,
    }
});

const rootFolder = ref({
    uid: null,
    children: [],
    toggled: false,
    documentable_type: 'folder',
    documentable: {
        title: 'Main folder',
        uid: null,
    },
});
const documents = ref([]);

const perPage = ref(100)
const currentPage = ref(1)

const loadingData = ref(false);

// loaders host the uid of the folder for which we are loading subfolders
const loaders = ref([]);

function closeModal() {
    emit('close')
}

function moveDocument() {
    emit('move', targetFolder.value);
}

const findFolderByUid = (array, uid) => {
    for (let document of array) {
        if (document.documentable.uid === uid) {
            return document;
        }
        if (document.children) {
            const found = findFolderByUid(document.children, uid);
            if (found) {
                return found;
            }
        }
    }
    return null;
};

const listDocuments = async (page = 1, folder = null, parentUid = null) => {
    try {
        const response = await list(organization.value.slug, page, perPage.value, '', 'title', folder)
        const documentsData = response.data.filter(document => document.documentable_type === 'folder').map(item => {
            return {
                ...item,
                toggled: false,
            }
        });

        if (!Array.isArray(documentsData)) {
            throw new Error('Response data is not an array'); // Safeguard to ensure response is an array
        }

        if (parentUid) {
            // Find the folder by its UID and populate its children
            const parentFolder = findFolderByUid(documents.value, parentUid);
            if (!parentFolder.children) {
                parentFolder.children = [];
            }
            parentFolder.children = [...parentFolder.children, ...documentsData];
        } else {
            // Append data to the root level documents array
            documents.value = [...documents.value, ...documentsData];
        }

        // If there's a meta object for pagination (assuming this might be present in a future response)
        // Replace the `meta.current_page < meta.total` block with the real pagination logic if needed.
    } catch (error) {
        console.error('Error fetching documents:', error);
    }
};

function selectFolder(folder) {
    if(targetFolder.value && folder.uid === targetFolder.value.uid) {
        targetFolder.value = null;
        return;
    }

    targetFolder.value = folder;
}
const toggleFolder = async (document) => {
    const uid = document.documentable.uid;

    currentPage.value = 1;

    // Find the folder object by its uid
    const folder = findFolderByUid(documents.value, uid);

    // Reset the children array if it's already populated (optional)
    folder.children = [];

    // Fetch the child documents for this folder
    folder.toggled = !folder.toggled;

    if(folder.toggled) {
        loaders.value.push(document.uid);
        try {
            await listDocuments(currentPage.value, uid, uid);
        } catch (e) {
            toast.error('Error loading the folder contents');
        } finally {
            loaders.value = loaders.value.filter(loader => loader !== document.uid);
        }
    }
};

const formattedDocuments = computed(() => {
    return filterDocuments(documents.value);
});

function filterDocuments(docs) {
    return docs.reduce((filtered, document) => {
        // Skip the document if it's selected
        if (props.selectedDocuments.include.includes(document.uid) || (props.singleFile && props.singleFile.uid === document.uid)) {
            return filtered;
        }

        // If the document has children, filter them recursively
        const doc = { ...document };
        if (doc.documentable.has_children && doc.children) {
            doc.children = filterDocuments(doc.children).filter(child => {
                return !props.selectedDocuments.include.includes(child.uid) || (props.singleFile && props.singleFile.uid !== child.uid);
            });
        }

        // Only add the document to the filtered list if it passed the filters
        filtered.push(doc);
        return filtered;
    }, []);
}

// Fetch documents for the root folder when component is mounted
onMounted(async () => {
    loadingData.value = true;

    await listDocuments(currentPage.value, null);

    loadingData.value = false;
});
</script>